import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { VioTablesV2Component } from 'vio-tables-v2';
import type { VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { ApiRequestsService } from '../../services/api-requests.service';

declare const bootstrap: any;

interface VarConfig {
    type: 'hardcoded' | 'userField';
    value: string;
    fieldName: string;
}

const USER_MASTER_FIELDS = [
    { fieldName: 'name',             label: 'Full Name' },
    { fieldName: 'firstName',        label: 'First Name' },
    { fieldName: 'lastName',         label: 'Last Name' },
    { fieldName: 'organisationName', label: 'Organisation' },
    { fieldName: 'empNo',            label: 'Employee No.' },
    { fieldName: 'whatsAppNo',       label: 'WhatsApp No.' },
];

interface ApprovedTemplate {
    _id: string;
    templateName: string;
    language: string;
    templateType: 'standard' | 'engage';
    templateFormat?: 'text' | 'image' | 'carousel';
    cardCount: number;
    outerBodyText: string;
    cardBodyTexts: string[];
    buttonNames: string[];
    cardImageUrls?: (string | null)[];
}

interface SavedCampaign {
    _id: string;
    campaignName: string;
    templateName: string;
    templateType: string;
    outerVariable: string;
    cardVariables: string[];
    outerVarConfigs?: VarConfig[];
    cardVarConfigs?: VarConfig[][];
    createdAt: string;
}

interface DeliveryData {
    summary: { total: number; sent: number; delivered: number; read: number; failed: number };
    logs: any[];
}

@Component({
    selector: 'app-campaign',
    standalone: true,
    imports: [CommonModule, FormsModule, VioTablesV2Component],
    templateUrl: './campaign.component.html',
    styleUrl: './campaign.component.scss',
})
export class CampaignComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('runsModal') runsModalRef?: ElementRef<HTMLElement>;
    @ViewChild('deliveryModal') deliveryModalRef?: ElementRef<HTMLElement>;
    @ViewChild('recipientsModal') recipientsModalRef?: ElementRef<HTMLElement>;
    @ViewChild('testMessageModal') testMessageModalRef?: ElementRef<HTMLElement>;

    // Form visibility
    showCreateForm = false;

    // Form state
    campaignName = '';
    selectedTemplate: ApprovedTemplate | null = null;
    outerVarConfigs: VarConfig[] = [];
    cardVarConfigs: VarConfig[][] = [];
    readonly userMasterFields = USER_MASTER_FIELDS;
    private _lastCampaignNameSynced = '';

    // Template picker
    approvedTemplates: ApprovedTemplate[] = [];
    isLoadingApprovedTemplates = false;

    // Save state
    isSaving = false;
    resultMessage = '';
    resultSuccess = false;

    // Sample user for preview
    sampleUser: any = null;

    // Saved campaigns list
    savedCampaigns: SavedCampaign[] = [];
    savedCampaignsForTable: any[] = [];
    isLoadingCampaigns = false;

    // Runs modal
    selectedCampaignForRuns: SavedCampaign | null = null;
    campaignRunsForModal: any[] = [];
    isLoadingCampaignRuns = false;

    // Delivery modal (from runs)
    deliveryData: DeliveryData | null = null;
    isLoadingDelivery = false;
    deliveryRunLabel = '';

    // Recipients modal (from runs)
    recipientsLabel = '';
    recipientsData: any[] = [];

    // Test message modal
    testPhoneNo = '';
    isSendingTest = false;
    testMsgResult = '';
    testMsgSuccess = false;

    private readonly STATUS_LABEL: Record<number, string> = { 10: 'Failed', 20: 'Sent', 30: 'Delivered', 40: 'Seen' };
    statusLabel(code: number): string { return this.STATUS_LABEL[code] ?? 'Unknown'; }

    savedCampaignsTableConfig: VT2_TABLE_CONFIG = {
        tableTitle: '',
        columnLabels: {
            campaignName: 'Campaign',
            templateName: 'Template',
            type: 'Type',
            created: 'Created',
        },
        excludeFields: ['_id', 'outerVariable', 'outerVarConfigs', 'cardVarConfigs', 'cardVariables'],
        isSortable: true,
        showFormulaButton: false,
        showSummationButton: false,
        showHeaderGroupButton: false,
        dateFields: ['created'],
    };

    campaignRunsTableConfig: VT2_TABLE_CONFIG = {
        tableTitle: '',
        columnLabels: {
            triggeredAt: 'Triggered',
            status: 'Status',
            recipients: 'Recipients',
            sentCount: 'Sent',
            failedCount: 'Failed',
        },
        excludeFields: ['_id', 'rawCustomers'],
        isSortable: true,
        showFormulaButton: false,
        showSummationButton: false,
        showHeaderGroupButton: false,
        dateFields: ['triggeredAt'],
        statusField: 'status',
        statusClassMap: {
            completed: 'bg-success',
            failed: 'bg-danger',
            draft: 'bg-secondary',
        },
    };

    constructor(private apiService: ApiRequestsService) {}

    ngOnInit(): void {
        this.savedCampaignsTableConfig = {
            ...this.savedCampaignsTableConfig,
            actionButtons: [
                {
                    icon: '👁',
                    label: 'View Runs',
                    title: 'View all runs for this campaign',
                    onClick: (row: any) => this.openRunsModal(row),
                },
            ],
        };

        this.campaignRunsTableConfig = {
            ...this.campaignRunsTableConfig,
            actionButtons: [
                {
                    icon: '👥',
                    label: 'Recipients',
                    title: 'Show recipient list for this run',
                    onClick: (row: any) => this.showRunRecipients(row),
                },
                {
                    icon: '📊',
                    label: 'Msg Status',
                    title: 'View message delivery status',
                    onClick: (row: any) => this.fetchRunDelivery(row),
                },
            ],
        };

        this.loadApprovedTemplates();
        this.loadSavedCampaigns();
        this.loadSampleUser();
    }

    ngAfterViewInit(): void {
        if (this.runsModalRef) document.body.appendChild(this.runsModalRef.nativeElement);
        if (this.deliveryModalRef) document.body.appendChild(this.deliveryModalRef.nativeElement);
        if (this.recipientsModalRef) document.body.appendChild(this.recipientsModalRef.nativeElement);
        if (this.testMessageModalRef) document.body.appendChild(this.testMessageModalRef.nativeElement);
    }

    ngOnDestroy(): void {
        [this.runsModalRef, this.deliveryModalRef, this.recipientsModalRef, this.testMessageModalRef].forEach(ref => {
            const el = ref?.nativeElement;
            if (el?.parentNode === document.body) document.body.removeChild(el);
        });
    }

    loadSampleUser(): void {
        this.apiService.getApiCall('api/v1/user-master').subscribe({
            next: (res: any) => {
                const users: any[] = Array.isArray(res?.Data) ? res.Data : [];
                this.sampleUser = users.find(u => u.firstName?.trim() || u.name?.trim() || u.lastName?.trim()) ?? users[0] ?? null;
            },
            error: () => {},
        });
    }

    loadApprovedTemplates(): void {
        this.isLoadingApprovedTemplates = true;
        this.apiService.getApiCall('api/v1/campaign/templates/approved').subscribe({
            next: (res: any) => {
                this.isLoadingApprovedTemplates = false;
                this.approvedTemplates = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
            },
            error: () => { this.isLoadingApprovedTemplates = false; },
        });
    }

    getVarCount(text: string): number {
        const matches = (text || '').match(/\{\{\d+\}\}/g) || [];
        if (!matches.length) return 0;
        return Math.max(...matches.map(m => parseInt(m.replace(/\{\{|\}\}/g, ''), 10)));
    }

    onTemplateSelect(templateId: string): void {
        this.selectedTemplate = this.approvedTemplates.find(t => t._id === templateId) || null;
        if (this.selectedTemplate) {
            const outerCount = this.getVarCount(this.selectedTemplate.outerBodyText);
            this.outerVarConfigs = Array.from({ length: outerCount }, (_, i) => ({
                type: 'hardcoded' as const,
                value: i === 0 ? this.campaignName : '',
                fieldName: '',
            }));
            this.cardVarConfigs = (this.selectedTemplate.cardBodyTexts || []).map(cardText => {
                const count = this.getVarCount(cardText);
                return Array.from({ length: count }, () => ({
                    type: 'hardcoded' as const, value: '', fieldName: '',
                }));
            });
        } else {
            this.outerVarConfigs = [];
            this.cardVarConfigs = [];
        }
    }

    onCampaignNameChange(): void {
        const first = this.outerVarConfigs[0];
        if (first?.type === 'hardcoded' && (!first.value || first.value === this._lastCampaignNameSynced)) {
            this.outerVarConfigs[0] = { ...first, value: this.campaignName };
        }
        this._lastCampaignNameSynced = this.campaignName;
    }

    trackByIndex(index: number): number { return index; }

    hasOuterVar(): boolean {
        return this.getVarCount(this.selectedTemplate?.outerBodyText || '') > 0;
    }

    hasCardVar(i: number): boolean {
        return this.getVarCount(this.selectedTemplate?.cardBodyTexts?.[i] || '') > 0;
    }

    templateFormatLabel(t: ApprovedTemplate): string {
        const fmt = t.templateFormat || 'carousel';
        if (fmt === 'text') return `Text · ${t.templateType}`;
        if (fmt === 'image') return `Image · ${t.templateType}`;
        return `${t.cardCount} card${t.cardCount === 1 ? '' : 's'} · ${t.templateType}`;
    }

    previewBodyText(text: string, configs: VarConfig[]): string {
        const substituted = (text || '').replace(/\{\{(\d+)\}\}/g, (_, n) => {
            const idx = parseInt(n, 10) - 1;
            const cfg = configs[idx];
            if (!cfg) return `{{${n}}}`;
            if (cfg.type === 'userField' && cfg.fieldName) {
                const sample = this.sampleUser != null ? String(this.sampleUser[cfg.fieldName] ?? '') : '';
                if (sample.trim()) return sample;
                const f = USER_MASTER_FIELDS.find(f => f.fieldName === cfg.fieldName);
                return f ? `[${f.label}]` : '[field]';
            }
            return cfg.value?.trim() || `[variable ${n}]`;
        });
        return substituted
            .replace(/\*([^*\n]+)\*/g, '$1')
            .replace(/_([^_\n]+)_/g, '$1')
            .replace(/~([^~\n]+)~/g, '$1');
    }

    get tplFormat(): 'text' | 'image' | 'carousel' {
        return this.selectedTemplate?.templateFormat || 'carousel';
    }

    loadSavedCampaigns(): void {
        this.isLoadingCampaigns = true;
        this.apiService.getApiCall('api/v1/campaign/list').subscribe({
            next: (res: any) => {
                this.isLoadingCampaigns = false;
                this.savedCampaigns = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
                this.savedCampaignsForTable = this.savedCampaigns.map(c => ({
                    _id: c._id,
                    campaignName: c.campaignName,
                    templateName: c.templateName || '—',
                    type: c.templateType || '—',
                    outerVariable: c.outerVariable || '—',
                    created: c.createdAt,
                }));
            },
            error: () => { this.isLoadingCampaigns = false; },
        });
    }

    get isValid(): boolean {
        return !!this.campaignName.trim() && !!this.selectedTemplate;
    }

    get validationHint(): string {
        if (!this.campaignName.trim()) return 'Enter a campaign name.';
        if (!this.selectedTemplate) return 'Select an approved template.';
        return '';
    }

    save(): void {
        if (!this.isValid || this.isSaving) return;
        this.isSaving = true;
        this.resultMessage = '';

        const fd = new FormData();
        fd.append('campaignName', this.campaignName.trim());
        fd.append('selectedTemplateId', this.selectedTemplate!._id);
        fd.append('outerVarConfigs', JSON.stringify(this.outerVarConfigs));
        fd.append('cardVarConfigs', JSON.stringify(this.cardVarConfigs));
        fd.append('outerVariable',
            this.outerVarConfigs.map(c => c.type === 'hardcoded' ? c.value.trim() : c.fieldName).join(','));
        fd.append('cardVariables', JSON.stringify(
            this.cardVarConfigs.map(cfgs =>
                cfgs.map(c => c.type === 'hardcoded' ? c.value.trim() : c.fieldName).join(',')
            )
        ));

        this.apiService.uploadMultiple(fd, 'api/v1/campaign/create').subscribe({
            next: (res: any) => {
                this.isSaving = false;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    this.resultMessage = `Campaign "${this.campaignName.trim()}" saved. Go to Trigger Campaign to send it.`;
                    this.resultSuccess = true;
                    this.resetForm();
                    this.showCreateForm = false;
                    this.loadSavedCampaigns();
                } else {
                    this.resultMessage = res?.errorDescription || 'Failed to save campaign.';
                    this.resultSuccess = false;
                }
            },
            error: () => {
                this.isSaving = false;
                this.resultMessage = 'Failed to save campaign. Please check your connection.';
                this.resultSuccess = false;
            },
        });
    }

    // ── Runs modal ──────────────────────────────────────────────────────────────

    openRunsModal(row: any): void {
        this.selectedCampaignForRuns = this.savedCampaigns.find(c => c._id === row._id) || null;
        this.campaignRunsForModal = [];
        this.isLoadingCampaignRuns = true;
        bootstrap.Modal.getOrCreateInstance(this.runsModalRef!.nativeElement).show();
        this.apiService.getApiCall(`api/v1/campaign/${row._id}/runs`).subscribe({
            next: (res: any) => {
                this.isLoadingCampaignRuns = false;
                const runs: any[] = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
                this.campaignRunsForModal = runs.map(r => ({
                    _id: r._id,
                    triggeredAt: r.triggeredAt,
                    status: r.status,
                    recipients: r.customers?.length ?? 0,
                    rawCustomers: r.customers || [],
                    sentCount: r.sentCount,
                    failedCount: r.failedCount,
                }));
            },
            error: () => { this.isLoadingCampaignRuns = false; },
        });
    }

    showRunRecipients(row: any): void {
        this.recipientsLabel = row.triggeredAt
            ? new Date(row.triggeredAt).toLocaleString()
            : 'Run';
        this.recipientsData = row.rawCustomers || [];
        bootstrap.Modal.getOrCreateInstance(this.recipientsModalRef!.nativeElement).show();
    }

    fetchRunDelivery(row: any): void {
        this.deliveryData = null;
        this.deliveryRunLabel = row.triggeredAt
            ? new Date(row.triggeredAt).toLocaleString()
            : 'Run';
        this.isLoadingDelivery = true;
        bootstrap.Modal.getOrCreateInstance(this.deliveryModalRef!.nativeElement).show();
        this.apiService.getApiCall(`api/v1/campaign/runs/${row._id}/delivery`).subscribe({
            next: (res: any) => {
                this.isLoadingDelivery = false;
                if (res?.errorCode === this.apiService.NO_ERROR) this.deliveryData = res.Data;
            },
            error: () => { this.isLoadingDelivery = false; },
        });
    }

    // ── Test message ────────────────────────────────────────────────────────────

    openTestMessageModal(): void {
        this.testPhoneNo = '';
        this.testMsgResult = '';
        this.testMsgSuccess = false;
        bootstrap.Modal.getOrCreateInstance(this.testMessageModalRef!.nativeElement).show();
    }

    async sendTestMessage(): Promise<void> {
        if (!this.testPhoneNo.trim() || !this.selectedTemplate) return;
        this.isSendingTest = true;
        this.testMsgResult = '';
        try {
            const fd = new FormData();
            fd.append('templateId', this.selectedTemplate._id);
            fd.append('outerVarConfigs', JSON.stringify(this.outerVarConfigs));
            fd.append('cardVarConfigs', JSON.stringify(this.cardVarConfigs));
            fd.append('toNumber', this.testPhoneNo.trim());
            const res: any = await this.apiService.uploadMultiple(fd, 'api/v1/campaign/test-message').toPromise();
            if (res?.errorCode === this.apiService.NO_ERROR) {
                this.testMsgResult = `Test message sent to ${this.testPhoneNo.trim()}.`;
                this.testMsgSuccess = true;
            } else {
                this.testMsgResult = res?.errorDescription || 'Failed to send test message.';
                this.testMsgSuccess = false;
            }
        } catch {
            this.testMsgResult = 'Failed to send test message. Please check your connection.';
            this.testMsgSuccess = false;
        } finally {
            this.isSendingTest = false;
        }
    }

    private resetForm(): void {
        this.campaignName = '';
        this.selectedTemplate = null;
        this.outerVarConfigs = [];
        this.cardVarConfigs = [];
        this._lastCampaignNameSynced = '';
    }
}
